import { TeamRole } from '../../../server/graphql/__generated.types';
import { UserContextQuery } from '../../context/user/operation.generated';
import { TeamSeatCountQuery } from './operation.generated';

export type UserTeam = UserContextQuery['user']['team'];
export type UserTeamMember = NonNullable<UserTeam>['members'][number];

type TeamWithMembers<TMember extends { id: string }> = {
  members: TMember[];
};

export const findTeamMember = <TMember extends { id: string }>(
  team: TeamWithMembers<TMember> | null | undefined,
  userId: string
): TMember | undefined => team?.members.find(member => member.id === userId);

export type TeamRoleQuotas = NonNullable<
  TeamSeatCountQuery['user']['team']
>['seatCount']['roleQuotas'];

export enum TeamSeatType {
  Paid = 'Full',
  Quota = 'Quota',
}

export interface TeamSeats {
  /**
   * Should include total team members and pending invitations
   */
  current: number;
  /**
   * Max seats allocated for team
   */
  limit: number;

  /**
   * Current and total count for given roles
   */
  roleQuotas?: TeamRoleQuotas;
}

/**
 * Returns formatted string for
 */
export const getSeatCountLabel = (current: number, total: number) => {
  if (!total) return '';
  return `${current}/${total} seats used`;
};

/**
 * If no type provided, returns true if any seats are available
 */
export const assertSeatsAvailable = (
  seatCount: TeamSeats,
  type?: TeamSeatType
): boolean => {
  const { current, limit, roleQuotas } = seatCount;

  const hasPaidSeatsAvailable = current + 1 <= limit;

  const hasQuotaSeatsAvailable =
    roleQuotas?.some(roleQuota => {
      if (!roleQuota) return false;
      return roleQuota.current + 1 <= roleQuota.total;
    }) || false;

  if (type === TeamSeatType.Quota) return hasQuotaSeatsAvailable;
  if (type === TeamSeatType.Paid) return hasPaidSeatsAvailable;

  return hasQuotaSeatsAvailable || hasPaidSeatsAvailable;
};

export const getTeamSeatTypeFromRole = (
  seatCount: TeamSeats,
  role: TeamRole
) => {
  const result = seatCount.roleQuotas?.find(
    roleQuota => roleQuota?.role === role
  );
  const isQuota = Boolean(result);
  return isQuota ? TeamSeatType.Quota : TeamSeatType.Paid;
};

export const convertToSeatCount = (query?: TeamSeatCountQuery) => {
  const { team } = query?.user || {};
  const { seatCount } = team || {};

  return {
    current: seatCount?.paidSeatsUsed || 0,
    limit: seatCount?.paidSeatsTotal || 0,
    roleQuotas: seatCount?.roleQuotas,
  };
};

export const getTotalMemberCount = (teamSeats: TeamSeats) => {
  return (
    teamSeats.current +
    (teamSeats.roleQuotas?.reduce((count, currentQuota) => {
      let total = count;
      if (currentQuota) {
        total += currentQuota.current;
      }
      return total;
    }, 0) || 0)
  );
};
